/**
 * @typedef {import("src/types").Restaurant} Restaurant
 */
import { html } from "htm/preact";
import { useAppContext } from "src/services/context.js";
import { decode } from "he";
import Button from "src/modules/reservations/components/button/button";
import settings from "src/services/settings";
import { Signal, useComputed, useSignal } from "@preact/signals";

/**
 * @param {{label: string;}} a
 * @param {{label: string;}} b
 */
function sorter(a, b) {
  return a.label < b.label ? -1 : 1;
}

/**
 * @typedef {object} LocationPickerProps
 * @prop {"reserve" | "order"} action
 * @prop {Signal<Restaurant>} initialLocation
 * @prop {boolean} isVisible
 * @prop {(id: string | number) => void} onSelect
 * @param {LocationPickerProps} props
 * @returns {import("preact").VNode}
 */
function LocationPicker(props) {
  const state = useAppContext();
  const countries = useComputed(() => {
    return Array.from(state.locationRegionsByCountry.value.byCountry.keys());
  });
  const selectedCountry = useSignal("");
  const regions = useComputed(() => {
    const selectedCountryValue =
      state.locationRegionsByCountry.value.byCountry.get(
        selectedCountry.value
      ) ?? [];
    return Array.from(selectedCountryValue)
      .map((r) => ({ label: r, value: r }))
      .sort(sorter);
  });
  const selectionText =
    props.action === "reserve"
      ? "To make a reservation at another Moxies location, please select your new location below:"
      : "To order pick-up or delivery at another Moxies location, please select your new location below:";
  const selectedRegion = useSignal("");
  const locations = useComputed(() => {
    const selectedRegionValue =
      state.locationRegionsByCountry.value.byRegion.get(selectedRegion.value) ??
      [];
    return Array.from(selectedRegionValue)
      .map((l) => {
        if (props.action === "reserve") {
          return { label: l.label, value: l.rid };
        }
        return { label: l.label, value: l.id };
      })
      .sort(sorter);
  });
  const selectedLocation = useSignal("");

  /**
   * @param {Event & { target: HTMLElement }} event
   */
  function handleCountryChange(event) {
    const value = event.target.getAttribute("value");
    selectedRegion.value = "";
    selectedLocation.value = "";
    selectedCountry.value = value;
  }

  /**
   * @param {Event & { target: HTMLElement }} event
   */
  function handleRegionChange(event) {
    const value = event.target.getAttribute("value");
    selectedRegion.value = value;
    selectedLocation.value = "";
  }
  /**
   * @param {Event & { target: HTMLElement }} event
   */
  function handleLocationChange(event) {
    const value = event.target.getAttribute("value");
    selectedLocation.value = value;
  }

  function handleSelectLocation() {
    if (selectedLocation.value) {
      props.onSelect(selectedLocation.value);
    }
  }

  function handleSelectDefaultLocation() {
    const location =
      Object.keys(props.initialLocation.value).length > 0
        ? props.initialLocation.value
        : state.selectedRestaurant.value;

    if (props.action === "reserve") {
      props.onSelect(location.acf?.opentable_rid);
    } else {
      props.onSelect(location.id);
    }
  }

  const title = props.initialLocation?.value.title?.rendered
    ? decode(props.initialLocation.value.title.rendered)
    : state.selectedLocation.value?.title;
  return html` <div
    class="mx-details mx-reservation-form__step ${props.isVisible
      ? "mx-reservation-form__step--active"
      : ""}"
  >
    <header class="mx-reservation-form__header">
      <div class="moxies-title">
        <small class="moxies-title__intro">Your Current Location is:</small>
        <h4 class="elementor-heading-title elementor-size-default">
            <span class="moxies-title-default">Moxies</span>
          <span class="moxies-italic"> ${title} </span>
        </h4>
      </div>
      <div class="mx-reservation-form__address">
        ${props.initialLocation?.value.acf?.street_address}
      </div>
      <${Button}
        icon=${settings.svgArrowIcon}
        type="submit"
        onClick=${handleSelectDefaultLocation}
        >${props.action} at this Location<//
      >
      <p class="mx-location-picker__divider"><span>Or</span></p>
      <p>${selectionText}</p>
    </header>
    <div class="mx-location-picker">
      <form class="mx-location-picker__selects">
        <mx-select
          show-icon
          value=${selectedCountry}
          onChange=${handleCountryChange}
        >
          <select name="country">
            <option value="">Country</option>
            ${countries.value.map(
              (c) => html`<option key=${c} value=${c}>${c}</option>`
            )}
          </select>
        </mx-select>
        <mx-select
          show-icon
          onChange=${handleRegionChange}
          value=${selectedRegion}
          items=${JSON.stringify(regions.value)}
        >
          <select name="region">
            <option value="">Region</option>
            ${regions.value.map(
              (r) =>
                html`<option key=${r.value} value=${r.value}>
                  ${r.value}
                </option>`
            )}
          </select>
        </mx-select>
        <mx-select
          show-icon
          items=${JSON.stringify(locations.value)}
          value=${selectedLocation}
          onChange=${handleLocationChange}
        >
          <select name="location">
            <option value="">Location</option>
            ${locations.value.map(
              (l) =>
                html`<option key=${l.value} value=${l.value}>
                  ${l.label}
                </option>`
            )}
          </select>
        </mx-select>
      </form>
      <${Button}
        classes=${["find-a-time-moxies-button__bg"]}
        disabled=${!selectedLocation.value}
        icon=${settings.svgArrowIcon}
        type="submit"
        onClick=${handleSelectLocation}
        >Switch to this Location<//
      >
    </div>
  </div>`;
}

export default LocationPicker;
